import { FC, useEffect } from "react";

import { PageLimitProps, filterOutPagesInOtherSections, usePageLimits } from "./helper";
import { PagesCard } from "./topic/activity/detail/PagesCard";

import type { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { useHomeRole } from "providers/HomeRoleProvider";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentSectionId, selectReadyForAssessmentPages } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { PAGE_LIST_ITEM_FIELDS } from "utils/page";
import { useViewLevel } from "utils/urls";

const READY_FOR_ASSESSMENT_PAGE_FIELDS = [
  ...PAGE_LIST_ITEM_FIELDS,
  "late_submission_interval",
] as const satisfies readonly (keyof PageType)[];

type ReadyForAssessmentPagesListProps = PageLimitProps & {
  shouldLinkSidebar?: boolean;
};

// On home, filter by home persona across sections; otherwise current section.
export const ReadyForAssessmentPagesList: FC<ReadyForAssessmentPagesListProps> = ({
  showAll,
  onCountChanged,
  shouldLinkSidebar,
}) => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isHome = useViewLevel().home === true;
  const homeRole = useHomeRole();

  const readyforAssessmentPages = useListSelector(
    (s) => selectReadyForAssessmentPages(s, homeRole),
    READY_FOR_ASSESSMENT_PAGE_FIELDS,
  );
  const displayPages = usePageLimits(
    filterOutPagesInOtherSections(readyforAssessmentPages),
    showAll,
    onCountChanged,
  );

  const header = t(`section.sidebar.planner.ready_for_assessment`);

  useEffect(() => {
    if (!sectionId) {
      return;
    }

    storeApi.pages.list({ section_id: sectionId });
    storeApi.activities.list({ section_id: sectionId });
  }, [sectionId]);

  if (!displayPages.length) {
    return (
      <>
        <div className="body-s m-2 text-black-tint-40">{header}</div>
        <Card className="mb-3" small>
          <div className="body-s p-2 font-light text-black-tint-40">
            {t("section.sidebar.planner.no_pages_recently_assessed")}
          </div>
        </Card>
      </>
    );
  }

  return (
    <PagesCard
      header={header}
      pages={displayPages}
      shouldLinkSidebar={shouldLinkSidebar}
      showSectionTitle={isHome}
    />
  );
};
